import React, { useState } from 'react';
import { doc, updateDoc } from 'firebase/firestore';
import { auth, db, getHttpFunctionUrl, postJsonWithAuth } from '../firebase';
import ModalReautenticacao from './ModalReautenticacao';
import useFaturaNovaGuia, { fecharGuiaFatura, reservarGuiaFatura } from './useFaturaNovaGuia';

function AbaVitrinePlanos({ usuarioAtual, planoAtual, configGerais, setAbaAtiva }) {
  const { abrirFaturaNovaGuia, modalFatura } = useFaturaNovaGuia();
  const [isProcessando, setIsProcessando] = useState(false);
  const [tituloProcessamento, setTituloProcessamento] = useState('Processando assinatura...');
  const [mensagemProcessamento, setMensagemProcessamento] = useState('Estamos preparando sua solicitação. Não atualize a página.');

 // 🚀 ESTADOS DO CHECKOUT RÁPIDO
  const [exibirFormCheckout, setExibirFormCheckout] = useState(false);
  const [dadosFaltantes, setDadosFaltantes] = useState({ 
    razaoSocial: '', 
    cpfCnpj: '', 
    telefone: '', 
    cep: '', 
    rua: '', 
    numero: '', 
    bairro: '', 
    cidade: '', 
    estado: '' 
  });
  const [planoSelecionado, setPlanoSelecionado] = useState(null);
  const [isSalvandoDados, setIsSalvandoDados] = useState(false);
  const [exibirReautenticacao, setExibirReautenticacao] = useState(false);
  const [planoPendenteReautenticacao, setPlanoPendenteReautenticacao] = useState(null);
  const exigirReautenticacaoMudarPlano = configGerais?.reautenticacao_lojista_mudar_plano === true;

  // 🚀 FUNÇÃO INTERCEPTADORA
  const verificarDadosEAssinar = (plano) => {
    const d = usuarioAtual;
    // Verifica exatamente os campos que a AbaFinanceiro.js utiliza
    const temDados = d?.razaoSocial && d?.cpfCnpj && d?.telefone && d?.cep && d?.rua && d?.numero && d?.bairro && d?.cidade && d?.estado;

    if (temDados) {
      assinarPlano(plano);
    } else {
      setPlanoSelecionado(plano);
      setDadosFaltantes({
        razaoSocial: d?.razaoSocial || '',
        cpfCnpj: d?.cpfCnpj || '',
        telefone: d?.telefone || '',
        cep: d?.cep || '',
        rua: d?.rua || '',
        numero: d?.numero || '',
        bairro: d?.bairro || '',
        cidade: d?.cidade || '',
        estado: d?.estado || ''
      });
      setExibirFormCheckout(true);
    }
  };

  // 🚀 FUNÇÃO QUE SALVA E AVANÇA
  const salvarDadosESeguir = async () => {
    const { razaoSocial, cpfCnpj, telefone, cep, rua, numero, bairro, cidade, estado } = dadosFaltantes;
    
    if (!razaoSocial || !cpfCnpj || !telefone || !cep || !rua || !numero || !bairro || !cidade || !estado) {
      alert("⚠️ Por favor, preencha todos os campos para prosseguir.");
      return;
    }

    const guiaFatura = exigirReautenticacaoMudarPlano ? null : reservarGuiaFatura();
    setIsSalvandoDados(true);
    try {
      const uidCliente = auth.currentUser?.uid;
      if (!uidCliente) throw new Error("Usuário não autenticado");
      
      const userRef = doc(db, 'users', uidCliente);
      
      // Salva exatamente com as chaves que a AbaFinanceiro e o Servidor esperam
      await updateDoc(userRef, {
        razaoSocial,
        cpfCnpj,
        telefone,
        cep,
        rua,
        numero,
        bairro,
        cidade,
        estado
      });

      // Atualiza o objeto local para que a próxima função veja os dados
      Object.assign(usuarioAtual, dadosFaltantes);

      setExibirFormCheckout(false);
      await assinarPlano(planoSelecionado, false, guiaFatura);
    } catch (error) {
      fecharGuiaFatura(guiaFatura);
      console.error("Erro ao salvar dados:", error);
      alert("Erro ao salvar dados no perfil.");
    } finally {
      setIsSalvandoDados(false);
    }
  };

  const formatarPreco = (valor) => { 
    const valStr = Number(valor || 0).toFixed(2); 
    const [inteiro, centavos] = valStr.split('.'); 
    return { inteiro, centavos }; 
  };
  
  const precoBronze = formatarPreco(configGerais.preco_bronze || 49.90);
  const precoPrata = formatarPreco(configGerais.preco_prata || 89.90);
  const precoOuro = formatarPreco(configGerais.preco_ouro || 149.90);
  const precoDiamante = formatarPreco(configGerais.preco_diamante || 997.00);
  const limMensalGratuitoVisual = configGerais.limite_mensal_gratuito || 300;
  const assinaturaCanceladaAgendada = usuarioAtual?.status_assinatura === 'cancelada_agendada';
  const obterBotaoDesabilitado = (plano) => isProcessando || (planoAtual === plano && !assinaturaCanceladaAgendada);
  const obterRotuloBotao = (plano, rotuloPadrao) => {
    if (isProcessando) return 'Gerando...';
    if (planoAtual === plano && assinaturaCanceladaAgendada) {
      return `Reativar ${plano.charAt(0).toUpperCase()}${plano.slice(1)}`;
    }
    if (planoAtual === plano) return 'Ativo';
    return rotuloPadrao;
  };
  const solicitarReautenticacaoParaPlano = (planoEscolhido) => {
    setPlanoPendenteReautenticacao(planoEscolhido);
    setExibirReautenticacao(true);
  };

  const assinarPlano = async (planoEscolhido, ignorarReautenticacao = false, guiaReservada = null) => {
    // 1. Evita múltiplos cliques seguidos
    if (isProcessando) {
      fecharGuiaFatura(guiaReservada);
      return;
    }

    if (!ignorarReautenticacao && exigirReautenticacaoMudarPlano) {
      fecharGuiaFatura(guiaReservada);
      solicitarReautenticacaoParaPlano(planoEscolhido);
      return;
    }

    // 2. 🛡️ TRAVA INTELIGENTE
    const temLinkPendente = usuarioAtual?.link_fatura_pendente;
    const mesmoPlanoPendente = usuarioAtual?.plano_pendente_pagamento === planoEscolhido;

    if (temLinkPendente) {
      if (mesmoPlanoPendente) {
        // Se ele clicou no MESMO PLANO, não deixamos duplicar.
        alert("Você já possui um pagamento pendente para este plano. Vamos abrir o link para você.");
        abrirFaturaNovaGuia(usuarioAtual.link_fatura_pendente, guiaReservada || reservarGuiaFatura());
        return;
      } else {
        // Se ele mudou de ideia (ex: era Bronze e agora quer Ouro), avisamos mas deixamos seguir.
        const confirmarNovo = window.confirm(`Vimos que você tem um pagamento pendente de outro plano. Deseja ignorá-lo e gerar uma nova cobrança para o plano ${planoEscolhido.toUpperCase()}?`);
        if (!confirmarNovo) {
          fecharGuiaFatura(guiaReservada);
          return;
        }
        // Se ele aceitou, o código segue e gera um novo link no Asaas
      }
    }
    setIsProcessando(true);
    let guiaFatura = guiaReservada;
    try {
      const uidCliente = auth.currentUser?.uid;
      const emailCliente = auth.currentUser?.email;

      if (!uidCliente) {
        alert("Sessão não identificada. Por favor, recarregue a página.");
        setIsProcessando(false);
        return;
      }

      // ⚖️ A MÁGICA DA HIERARQUIA: 0=Gratuito, 1=Bronze, 2=Prata, 3=Ouro
      const pesos = { gratuito: 0, bronze: 1, prata: 2, ouro: 3, diamante: 4 };
      const pesoAtual = pesos[planoAtual] || 0;
      const pesoNovo = pesos[planoEscolhido] || 0;

      let urlEndpoint = "";
      let bodyData = {};
      let isDowngrade = false;
      let isReativacao = false;

      if (assinaturaCanceladaAgendada && pesoNovo > 0) {
        isReativacao = true;
        urlEndpoint = getHttpFunctionUrl('gerarAssinaturaPlano');
        bodyData = { plano: planoEscolhido, uidCliente: uidCliente, email: emailCliente };
        setTituloProcessamento('Reativando a assinatura...');
        setMensagemProcessamento('Estamos recriando a recorrência do pagamento. Não atualize a página.');
      }

      if (!isReativacao && pesoNovo < pesoAtual) {
        // 👇 É UM DOWNGRADE (Agendamento)
        if (!window.confirm(`Tem certeza que deseja reduzir para o plano ${planoEscolhido.toUpperCase()}? O valor menor será cobrado na sua próxima fatura, mas você continuará com os benefícios atuais até o fim do ciclo.`)) {
          setIsProcessando(false); return;
        }
        isDowngrade = true;
        urlEndpoint = getHttpFunctionUrl('agendarDowngradePlano');
        bodyData = { uidCliente: uidCliente, novoPlano: planoEscolhido };
        setTituloProcessamento('Agendando a mudança de plano...');
        setMensagemProcessamento('Estamos atualizando sua assinatura para o próximo ciclo. Não atualize a página.');
      } 
      else if (!isReativacao && pesoNovo > pesoAtual && pesoAtual !== 0) {
        // 👆 É UM UPGRADE (Cobra a diferença na hora)
        urlEndpoint = getHttpFunctionUrl('modificarAssinaturaAsaas');
        bodyData = { uidCliente: uidCliente, tipoAlteracao: "upgrade", novoPlano: planoEscolhido };
        setTituloProcessamento('Gerando seu pagamento...');
        setMensagemProcessamento('Estamos calculando a diferença do plano e abrindo o pagamento. Não atualize a página.');
      } 
      else if (!isReativacao) {
        // 🆕 É UMA PRIMEIRA ASSINATURA (Estava no gratuito)
        urlEndpoint = getHttpFunctionUrl('gerarAssinaturaPlano');
        bodyData = { plano: planoEscolhido, uidCliente: uidCliente, email: emailCliente };
        setTituloProcessamento('Gerando sua assinatura...');
        setMensagemProcessamento('Estamos preparando o link de pagamento da sua assinatura. Não atualize a página.');
      }

      if (!isDowngrade && !guiaFatura) guiaFatura = reservarGuiaFatura();
      const resposta = await postJsonWithAuth(urlEndpoint, bodyData);

      const data = await resposta.json();

      if (!resposta.ok) {
        throw new Error(data.erro || "Erro ao processar assinatura.");
      }

      if (isDowngrade) {
        // Se foi downgrade, não tem tela de pagamento para abrir!
        const dataFormatada = new Date(data.data_efetivacao + 'T12:00:00').toLocaleDateString('pt-BR');
        alert(`Downgrade agendado com sucesso! A mudança entrará em vigor no dia ${dataFormatada}.`);
      } else if (isReativacao && data.reativacaoAgendada && data.dataInicioNovaCobranca) {
        const dataFormatada = new Date(`${data.dataInicioNovaCobranca}T12:00:00`).toLocaleDateString('pt-BR');
        alert(`Nova assinatura preparada com sucesso! A nova cobrança ficará programada para ${dataFormatada}. Vamos abrir o pagamento para você concluir a reativação.`);
        abrirFaturaNovaGuia(data.linkPagamento, guiaFatura);
        guiaFatura = null;
      } else if (data.linkPagamento) {
        // Se foi upgrade ou assinatura nova, abre o Asaas
        abrirFaturaNovaGuia(data.linkPagamento, guiaFatura);
        guiaFatura = null;
      } else {
        alert("Não foi possível gerar o link de assinatura.");
      }

    } catch (error) {
      console.error("Erro ao assinar:", error);
      alert(error.message || "Erro ao abrir o banco. Tente novamente.");
    } finally {
      fecharGuiaFatura(guiaFatura);
      setIsProcessando(false);
    }
  };

  // 🔒 TRAVA DE SEGURANÇA PARA FILIAIS
  if (usuarioAtual?.id_usuario_pai) {
    return (
      <div className="routine-callout" style={{ textAlign: 'center', backgroundColor: '#f9f9f9', border: '1px solid #ddd' }}>
        <h3 className="routine-callout__title" style={{ color: '#d32f2f' }}>Acesso Restrito</h3>
        <p className="routine-callout__text" style={{ color: '#555' }}>
          Você está conectado como uma <b>Filial</b>. O gerenciamento de planos, upgrades e assinaturas é feito exclusivamente pela sua Loja Matriz.
        </p>
      </div>
    );
  }

  return (
    <div className="planos-panel" style={{ backgroundColor: '#f9f9f9', border: '1px solid #ddd' }}>
      {modalFatura}
      {isProcessando && (
        <div style={{ position: 'fixed', inset: 0, backgroundColor: 'rgba(15, 23, 42, 0.38)', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '20px', zIndex: 9997 }}>
          <div className="panel-compact-dialog" style={{ width: '100%', maxWidth: '420px', backgroundColor: '#fff', boxShadow: '0 20px 45px rgba(0,0,0,0.18)', textAlign: 'center' }}>
            <div style={{ width: '54px', height: '54px', borderRadius: '999px', margin: '0 auto 16px auto', backgroundColor: '#eef6ff', color: '#2563eb', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '24px', fontWeight: 'bold' }}>
              ...
            </div>
            <h3 style={{ marginTop: 0, marginBottom: '10px', color: '#1f2937' }}>{tituloProcessamento}</h3>
            <p style={{ margin: 0, color: '#6b7280', lineHeight: '1.6' }}>{mensagemProcessamento}</p>
          </div>
        </div>
      )}
      <ModalReautenticacao
        aberto={exibirReautenticacao}
        titulo="Confirmação reforçada"
        descricao="Confirme sua senha atual antes de gerar uma nova assinatura ou alterar o plano desta loja."
        textoBotao="Confirmar mudança"
        onFechar={() => {
          setExibirReautenticacao(false);
          setPlanoPendenteReautenticacao(null);
        }}
        onConfirmado={async () => {
          const planoConfirmado = planoPendenteReautenticacao;
          setExibirReautenticacao(false);
          setPlanoPendenteReautenticacao(null);

          if (planoConfirmado) {
            await assinarPlano(planoConfirmado, true);
          }
        }}
      />

      <div className="planos-heading">
        <h2 style={{ color: '#333' }}>Escolha o plano ideal para a sua loja</h2>
        <p style={{ color: '#666', maxWidth: '700px', margin: '0 auto' }}>Veja os recursos de cada plano e escolha o formato que melhor combina com a rotina da sua loja.</p>
      </div>
      {exibirFormCheckout && (
        <div className="planos-checkout" style={{ backgroundColor: '#f8f9fa', border: '1px solid #dee2e6' }}>
          <h3 style={{ marginTop: 0 }}>📝 Dados de Faturamento Obrigatórios</h3>
          
          <div style={{ display: 'flex', gap: '15px', flexWrap: 'wrap', marginBottom: '15px' }}>
            <input 
              type="text" placeholder="Razão Social / Nome Completo *" value={dadosFaltantes.razaoSocial}
              onChange={(e) => setDadosFaltantes({...dadosFaltantes, razaoSocial: e.target.value})}
              style={{ padding: '10px', borderRadius: '4px', border: '1px solid #ccc', flex: '2 1 300px' }}
            />
            <input 
              type="text" placeholder="CPF ou CNPJ *" value={dadosFaltantes.cpfCnpj}
              onChange={(e) => setDadosFaltantes({...dadosFaltantes, cpfCnpj: e.target.value})}
              style={{ padding: '10px', borderRadius: '4px', border: '1px solid #ccc', flex: '1 1 200px' }}
            />
          </div>

          <div style={{ display: 'flex', gap: '15px', flexWrap: 'wrap', marginBottom: '15px' }}>
            <input 
              type="text" placeholder="Telefone *" value={dadosFaltantes.telefone}
              onChange={(e) => setDadosFaltantes({...dadosFaltantes, telefone: e.target.value})}
              style={{ padding: '10px', borderRadius: '4px', border: '1px solid #ccc', flex: '1 1 180px' }}
            />
            <input 
              type="text" placeholder="CEP *" value={dadosFaltantes.cep}
              onChange={(e) => setDadosFaltantes({...dadosFaltantes, cep: e.target.value})}
              style={{ padding: '10px', borderRadius: '4px', border: '1px solid #ccc', flex: '1 1 120px' }}
            />
            <input 
              type="text" placeholder="Rua *" value={dadosFaltantes.rua}
              onChange={(e) => setDadosFaltantes({...dadosFaltantes, rua: e.target.value})}
              style={{ padding: '10px', borderRadius: '4px', border: '1px solid #ccc', flex: '2 1 250px' }}
            />
          </div>

          <div style={{ display: 'flex', gap: '15px', flexWrap: 'wrap' }}>
            <input 
              type="text" placeholder="Número *" value={dadosFaltantes.numero}
              onChange={(e) => setDadosFaltantes({...dadosFaltantes, numero: e.target.value})}
              style={{ padding: '10px', borderRadius: '4px', border: '1px solid #ccc', flex: '0 1 80px' }}
            />
            <input 
              type="text" placeholder="Bairro *" value={dadosFaltantes.bairro}
              onChange={(e) => setDadosFaltantes({...dadosFaltantes, bairro: e.target.value})}
              style={{ padding: '10px', borderRadius: '4px', border: '1px solid #ccc', flex: '1 1 150px' }}
            />
            <input 
              type="text" placeholder="Cidade *" value={dadosFaltantes.cidade}
              onChange={(e) => setDadosFaltantes({...dadosFaltantes, cidade: e.target.value})}
              style={{ padding: '10px', borderRadius: '4px', border: '1px solid #ccc', flex: '1 1 150px' }}
            />
            <input 
              type="text" placeholder="Estado (UF) *" value={dadosFaltantes.estado} maxLength="2"
              onChange={(e) => setDadosFaltantes({...dadosFaltantes, estado: e.target.value.toUpperCase()})}
              style={{ padding: '10px', borderRadius: '4px', border: '1px solid #ccc', flex: '0 1 80px' }}
            />
          </div>

          <div style={{ marginTop: '20px', display: 'flex', gap: '10px' }}>
            <button onClick={salvarDadosESeguir} disabled={isSalvandoDados} style={{ padding: '12px 25px', backgroundColor: '#28a745', color: '#fff', border: 'none', borderRadius: '4px', cursor: 'pointer', fontWeight: 'bold' }}>
              {isSalvandoDados ? 'Salvando...' : 'Confirmar e Gerar Fatura'}
            </button>
            <button onClick={() => setExibirFormCheckout(false)} style={{ padding: '12px 25px', backgroundColor: '#e9ecef', color: '#333', border: '1px solid #ccc', borderRadius: '4px', cursor: 'pointer' }}>
              Cancelar
            </button>
          </div>
        </div>
      )}
      <div className="planos-grid">
        
        {/* PLANO GRATUITO */}
        <div style={{ width: '260px', padding: '30px 20px', backgroundColor: 'white', border: planoAtual === 'gratuito' ? '3px solid #4CAF50' : '1px solid #ddd', borderRadius: '12px', textAlign: 'center', position: 'relative', boxShadow: '0 4px 6px rgba(0,0,0,0.05)' }}>
          {planoAtual === 'gratuito' && <div style={{ position: 'absolute', top: '-14px', left: '50%', transform: 'translateX(-50%)', backgroundColor: '#4CAF50', color: 'white', padding: '5px 15px', borderRadius: '15px', fontSize: '12px', fontWeight: 'bold' }}>SEU PLANO ATUAL</div>}
          <h3 style={{ color: '#555', fontSize: '20px' }}>Gratuito</h3>
          <h2 style={{ fontSize: '36px', margin: '20px 0', color: '#333' }}>R$ 0<small style={{fontSize: '14px', color: '#999'}}>/mês</small></h2>
          <ul style={{ listStyle: 'none', padding: 0, margin: '25px 0', textAlign: 'left', color: '#555', fontSize: '14px', lineHeight: '2' }}>
            <li>✔️ <b>{limMensalGratuitoVisual}</b> ofertas mensais</li>
            
          </ul>
          <button disabled style={{ padding: '12px', backgroundColor: '#eee', color: '#999', border: '1px solid #ccc', borderRadius: '6px', fontWeight: 'bold', cursor: 'not-allowed', position: 'absolute', bottom: '20px', left: '0', width: 'calc(100% - 40px)', margin: '0 20px' }}>
            {planoAtual === 'gratuito' ? 'Ativo' : 'Plano Básico'}
          </button>
        </div>

        {/* PLANO BRONZE */}
        <div style={{ width: '260px', padding: '30px 20px', backgroundColor: 'white', border: planoAtual === 'bronze' ? '3px solid #FF9800' : '1px solid #FFCC80', borderRadius: '12px', textAlign: 'center', position: 'relative', boxShadow: '0 8px 15px rgba(255, 152, 0, 0.15)' }}>
          {planoAtual === 'bronze' && <div style={{ position: 'absolute', top: '-14px', left: '50%', transform: 'translateX(-50%)', backgroundColor: '#FF9800', color: 'white', padding: '5px 15px', borderRadius: '15px', fontSize: '12px', fontWeight: 'bold' }}>SEU PLANO ATUAL</div>}
          <div style={{ position: 'absolute', top: '10px', right: '10px', fontSize: '24px' }}>🥉</div>
          <h3 style={{ color: '#F57C00', fontSize: '20px' }}>Bronze</h3>
          <h2 style={{ fontSize: '36px', margin: '20px 0', color: '#333' }}>R$ {precoBronze.inteiro}<small style={{fontSize: '14px', color: '#999'}}>,{precoBronze.centavos}/mês</small></h2>
          <ul style={{ listStyle: 'none', padding: 0, margin: '25px 0 60px 0', textAlign: 'left', color: '#555', fontSize: '14px', lineHeight: '2' }}>
            <li>✔️ Ofertas mensais ilimitadas</li>
            <li>✔️ <b>{configGerais.limite_bronze}</b> ofertas em lote por semana</li>
            <li style={{ color: '#F57C00', fontWeight: 'bold' }}>⭐ Mais praticidade para divulgar suas ofertas</li>
          </ul>
          <button onClick={() => verificarDadosEAssinar('bronze')} disabled={obterBotaoDesabilitado('bronze')} style={{ position: 'absolute', bottom: '20px', left: '0', width: 'calc(100% - 40px)', margin: '0 20px', padding: '12px', backgroundColor: (planoAtual === 'bronze' && !assinaturaCanceladaAgendada) ? '#eee' : '#FF9800', color: (planoAtual === 'bronze' && !assinaturaCanceladaAgendada) ? '#999' : 'white', border: 'none', borderRadius: '6px', fontWeight: 'bold', cursor: obterBotaoDesabilitado('bronze') ? 'not-allowed' : 'pointer', fontSize: '15px', transition: '0.3s' }}>
            {obterRotuloBotao('bronze', 'Assinar Bronze')}
          </button>
        </div>

        {/* PLANO PRATA */}
        <div style={{ width: '260px', padding: '30px 20px', backgroundColor: 'white', border: planoAtual === 'prata' ? '3px solid #00BCD4' : '2px solid #00BCD4', borderRadius: '12px', textAlign: 'center', position: 'relative', boxShadow: '0 8px 20px rgba(0, 188, 212, 0.2)' }}>
          {planoAtual !== 'prata' && <div style={{ position: 'absolute', top: '-14px', left: '50%', transform: 'translateX(-50%)', backgroundColor: '#00BCD4', color: 'white', padding: '5px 15px', borderRadius: '15px', fontSize: '12px', fontWeight: 'bold' }}>MAIS VENDIDO</div>}
          {planoAtual === 'prata' && <div style={{ position: 'absolute', top: '-14px', left: '50%', transform: 'translateX(-50%)', backgroundColor: '#00BCD4', color: 'white', padding: '5px 15px', borderRadius: '15px', fontSize: '12px', fontWeight: 'bold' }}>SEU PLANO ATUAL</div>}
          <div style={{ position: 'absolute', top: '10px', right: '10px', fontSize: '24px' }}>🥈</div>
          <h3 style={{ color: '#0097A7', fontSize: '20px' }}>Prata</h3>
          <h2 style={{ fontSize: '36px', margin: '20px 0', color: '#333' }}>R$ {precoPrata.inteiro}<small style={{fontSize: '14px', color: '#999'}}>,{precoPrata.centavos}/mês</small></h2>
          <ul style={{ listStyle: 'none', padding: 0, margin: '25px 0 60px 0', textAlign: 'left', color: '#555', fontSize: '14px', lineHeight: '2' }}>
            <li>✔️ Ofertas mensais ilimitadas</li>
            <li>✔️ Ofertas em lote ilimitadas</li>
            <li style={{ color: '#0097A7', fontWeight: 'bold' }}>⭐ Catálogo de produtos</li>
            <li style={{ color: '#0097A7', fontWeight: 'bold' }}>⭐ Mais agilidade para montar postagens</li>
          </ul>
          <button onClick={() => verificarDadosEAssinar('prata')} disabled={obterBotaoDesabilitado('prata')} style={{ position: 'absolute', bottom: '20px', left: '0', width: 'calc(100% - 40px)', margin: '0 20px', padding: '12px', backgroundColor: (planoAtual === 'prata' && !assinaturaCanceladaAgendada) ? '#eee' : '#00BCD4', color: (planoAtual === 'prata' && !assinaturaCanceladaAgendada) ? '#999' : 'white', border: 'none', borderRadius: '6px', fontWeight: 'bold', cursor: obterBotaoDesabilitado('prata') ? 'not-allowed' : 'pointer', fontSize: '15px', transition: '0.3s' }}>
             {obterRotuloBotao('prata', 'Assinar Prata')}
          </button>
        </div>

        {/* PLANO OURO */}
        <div style={{ width: '260px', padding: '30px 20px', backgroundColor: '#fff', border: planoAtual === 'ouro' ? '3px solid #FBC02D' : '1px solid #FFF59D', borderRadius: '12px', textAlign: 'center', position: 'relative', boxShadow: '0 8px 15px rgba(251, 192, 45, 0.15)' }}>
          {planoAtual === 'ouro' && <div style={{ position: 'absolute', top: '-14px', left: '50%', transform: 'translateX(-50%)', backgroundColor: '#FBC02D', color: '#333', padding: '5px 15px', borderRadius: '15px', fontSize: '12px', fontWeight: 'bold' }}>SEU PLANO ATUAL</div>}
          <div style={{ position: 'absolute', top: '10px', right: '10px', fontSize: '24px' }}>👑</div>
          <h3 style={{ color: '#FBC02D', fontSize: '20px' }}>Ouro</h3>
          <h2 style={{ fontSize: '36px', margin: '20px 0', color: '#333' }}>R$ {precoOuro.inteiro}<small style={{fontSize: '14px', color: '#999'}}>,{precoOuro.centavos}/mês</small></h2>
          <ul style={{ listStyle: 'none', padding: 0, margin: '25px 0 60px 0', textAlign: 'left', color: '#555', fontSize: '14px', lineHeight: '2' }}>
            <li>✔️ Ofertas mensais ilimitadas</li>
            <li>✔️ Ofertas em lote ilimitadas</li>
            <li style={{ color: '#FBC02D', fontWeight: 'bold' }}>⭐ Catálogo de produtos</li>
            <li style={{ color: '#FBC02D', fontWeight: 'bold' }}>⭐ Controle do alcance das postagens</li>
            <li style={{ color: '#FBC02D', fontWeight: 'bold' }}>⭐ Compartilhamento facilitado das postagens</li>
            <li style={{ color: '#002ae6', fontWeight: 'bold' }}>📝 Gestão de filiais (* Com taxa de inclusão por filial)</li>
          </ul>
          <button onClick={() => verificarDadosEAssinar('ouro')} disabled={obterBotaoDesabilitado('ouro')} style={{ position: 'absolute', bottom: '20px', left: '0', width: 'calc(100% - 40px)', margin: '0 20px', padding: '12px', backgroundColor: (planoAtual === 'ouro' && !assinaturaCanceladaAgendada) ? '#eee' : '#FBC02D', color: (planoAtual === 'ouro' && !assinaturaCanceladaAgendada) ? '#999' : '#333', border: 'none', borderRadius: '6px', fontWeight: 'bold', cursor: obterBotaoDesabilitado('ouro') ? 'not-allowed' : 'pointer', fontSize: '15px', transition: '0.3s' }}>
            {obterRotuloBotao('ouro', 'Assinar Ouro')}
          </button>
        </div>

          {/* PLANO DIAMANTE - ESTRATÉGICO */}
        <div style={{ width: '260px', padding: '30px 20px', backgroundColor: '#0f172a', border: planoAtual === 'diamante' ? '3px solid #3b82f6' : '1px solid #1e293b', borderRadius: '12px', textAlign: 'center', position: 'relative', boxShadow: '0 8px 15px rgba(0,0,0,0.3)' }}>
          <div style={{ position: 'absolute', top: '-14px', left: '50%', transform: 'translateX(-50%)', backgroundColor: '#3b82f6', color: 'white', padding: '5px 15px', borderRadius: '15px', fontSize: '12px', fontWeight: 'bold' }}>ESTRATÉGICO</div>
          <div style={{ position: 'absolute', top: '10px', right: '10px', fontSize: '24px' }}>💎</div>
          <h3 style={{ color: '#3b82f6', fontSize: '20px' }}>Diamante</h3>
          <h2 style={{ fontSize: '36px', margin: '20px 0', color: 'white' }}>R$ {precoDiamante.inteiro}<small style={{fontSize: '14px', color: '#94a3b8'}}>,{precoDiamante.centavos}/mês</small></h2>
          
          <ul style={{ listStyle: 'none', padding: 0, margin: '25px 0 60px 0', textAlign: 'left', color: '#cbd5e1', fontSize: '14px', lineHeight: '2' }}>
            <li>✅ <b>Tudo do Plano Ouro</b></li>
            <li style={{ color: '#60a5fa', fontWeight: 'bold' }}>📰 Encarte do Lojista</li>
            <li style={{ color: '#60a5fa', fontWeight: 'bold' }}>🧠 Inteligência de mercado</li>
            <li style={{ color: '#60a5fa', fontWeight: 'bold' }}>📊 Relatórios da rede (matriz e filiais)</li>
          </ul>
          
          <button onClick={() => window.open('https://wa.me/5547991660921?text=Olá! Tenho interesse no Plano Diamante para a minha loja.', '_blank')} style={{ position: 'absolute', bottom: '20px', left: '0', width: 'calc(100% - 40px)', margin: '0 20px', padding: '12px', backgroundColor: '#3b82f6', color: 'white', border: 'none', borderRadius: '6px', fontWeight: 'bold', cursor: 'pointer', fontSize: '15px', transition: '0.3s' }}>
            Falar com Especialista
          </button>
          
        </div>
      </div>
    </div>
  );
}

export default AbaVitrinePlanos;
